import type { Conflict, ConflictContractOwner } from '@app-types/conflict';

// A contract of a division conflict, flattened so a contract-level conflict and a
// meeting bundle render and export through the same path.
export interface DivisionConflictContract {
  id: number;
  contract_number: string;
  name: string;
  supplier_id: number | null;
  supplier_name: string | null;
}

// Data columns of the Division Conflicts list. The page and the CSV export both take their keys and
// labels from here, so a column can't be renamed or added on one side only.
export const DIVISION_CONFLICT_COLUMN_LABELS = {
  contract_number: 'Contract Number',
  contract_name: 'Contract Name',
  supplier: 'Supplier Name',
  owners: 'Contract Owners',
  detected: 'Detected'
} as const;

export type DivisionConflictColumnKey = keyof typeof DIVISION_CONFLICT_COLUMN_LABELS;

export const isDivisionConflictColumnKey = (key: string): key is DivisionConflictColumnKey =>
  key in DIVISION_CONFLICT_COLUMN_LABELS;

export interface DivisionConflictSupplier {
  id: number | null;
  name: string;
}

// A division conflict is raised either on a single contract or on a meeting that bundles
// several contracts; for a meeting, entity_id is a meeting id, not a contract id.
export const isMeetingConflict = (conflict: Conflict): boolean => conflict.entity_type === 'meeting';

export const getConflictContracts = (conflict: Conflict): DivisionConflictContract[] => {
  const info = conflict.entity_info;
  if (!isMeetingConflict(conflict)) {
    if (!info?.contract_number) return [];
    return [
      {
        id: conflict.entity_id,
        contract_number: info.contract_number,
        name: info.contract_name ?? '',
        supplier_id: info.supplier_id ?? null,
        supplier_name: info.supplier_name ?? null
      }
    ];
  }

  return (info?.contracts ?? []).map((contract) => ({
    id: contract.id,
    contract_number: contract.contract_number,
    name: contract.name,
    supplier_id: contract.supplier_id ?? null,
    supplier_name: contract.supplier_name
  }));
};

// Suppliers are de-duplicated by id (by name when a contract has no supplier id).
export const getConflictSuppliers = (conflict: Conflict): DivisionConflictSupplier[] => {
  const unique = new Map<string, DivisionConflictSupplier>();
  getConflictContracts(conflict).forEach((contract) => {
    if (!contract.supplier_name) return;
    const key = contract.supplier_id === null ? `name:${contract.supplier_name}` : `id:${contract.supplier_id}`;
    if (!unique.has(key)) unique.set(key, { id: contract.supplier_id, name: contract.supplier_name });
  });
  return Array.from(unique.values());
};

// Owners come from entity_info (live data). conflict.context only holds the snapshot taken
// when the conflict was detected, so it goes stale once owners change.
export const getConflictOwners = (conflict: Conflict): ConflictContractOwner[] => {
  if (!isMeetingConflict(conflict)) return conflict.entity_info?.contract_owners ?? [];

  const uniqueOwners = new Map<number, ConflictContractOwner>();
  (conflict.entity_info?.contracts ?? []).forEach((contract) => {
    contract.owners.forEach((owner) => uniqueOwners.set(owner.user_id, owner));
  });
  return Array.from(uniqueOwners.values());
};
